import type { MeetingContext } from "../captures/schema";

const CALENDAR_API = "https://www.googleapis.com/calendar/v3/calendars/primary/events";
const DEFAULT_WINDOW_MIN = 15;

interface GoogleAttendee {
  email?: string;
  displayName?: string;
}

interface GoogleEvent {
  id: string;
  /** `default`, `workingLocation`, `birthday`, `outOfOffice`, `focusTime`,
   *  `fromGmail`. Absent sur les agendas/réponses qui ne le renseignent pas. */
  eventType?: string;
  summary?: string;
  description?: string;
  htmlLink?: string;
  start?: { dateTime?: string; date?: string };
  end?: { dateTime?: string; date?: string };
  attendees?: GoogleAttendee[];
}

function eventDate(slot?: { dateTime?: string; date?: string }): Date | null {
  if (!slot) return null;
  const raw = slot.dateTime ?? slot.date;
  if (!raw) return null;
  const d = new Date(raw);
  return Number.isNaN(d.getTime()) ? null : d;
}

function eventToContext(event: GoogleEvent): MeetingContext | null {
  const start = eventDate(event.start);
  const end = eventDate(event.end);
  if (!start || !end) return null;
  return {
    event_id: event.id,
    title: event.summary ?? null,
    description: event.description ?? null,
    attendees: (event.attendees ?? []).map((a) => ({
      name: a.displayName ?? null,
      email: a.email ?? null,
    })),
    start_iso: start.toISOString(),
    end_iso: end.toISOString(),
    html_link: event.htmlLink ?? null,
  };
}

/**
 * Choisit l'événement qui correspond le mieux au moment présent :
 *  - en cours (start <= now <= end) en priorité
 *  - sinon le prochain qui démarre dans les `windowMin` minutes
 *  - sinon null
 */
export function pickCurrentEvent(
  events: GoogleEvent[],
  now: Date,
  windowMin = DEFAULT_WINDOW_MIN,
): MeetingContext | null {
  const windowMs = windowMin * 60_000;
  const nowMs = now.getTime();

  for (const event of events) {
    const start = eventDate(event.start);
    const end = eventDate(event.end);
    if (!start || !end) continue;
    if (start.getTime() <= nowMs && nowMs <= end.getTime()) {
      return eventToContext(event);
    }
  }

  for (const event of events) {
    const start = eventDate(event.start);
    if (!start) continue;
    const delta = start.getTime() - nowMs;
    if (delta > 0 && delta <= windowMs) {
      return eventToContext(event);
    }
  }

  return null;
}

export type FetchMeetingResult =
  | { kind: "found"; meeting: MeetingContext }
  | { kind: "none" }
  | { kind: "error"; status: number; message: string };

interface FetchInput {
  accessToken: string;
  now?: Date;
  windowMin?: number;
  fetchImpl?: typeof fetch;
}

export async function fetchCurrentMeeting(input: FetchInput): Promise<FetchMeetingResult> {
  const now = input.now ?? new Date();
  const windowMin = input.windowMin ?? DEFAULT_WINDOW_MIN;
  const doFetch = input.fetchImpl ?? fetch;

  const timeMin = new Date(now.getTime() - 30 * 60_000).toISOString();
  const timeMax = new Date(now.getTime() + windowMin * 60_000 + 5 * 60_000).toISOString();
  // `eventTypes=default` : ne demander QUE les vraies rencontres. Sans ce
  // paramètre l'API renvoie TOUS les types — dont `workingLocation`,
  // `birthday`, `outOfOffice`. Comme `timeMin`/`timeMax` bornent un
  // CHEVAUCHEMENT, une entrée « lieu de travail » de 00:00 à 23:59 chevauche
  // toutes les fenêtres, sort première sous `orderBy=startTime`, et devient le
  // contexte de la capture : mauvais titre dans les prompts, `attendees` vide.
  const url = `${CALENDAR_API}?singleEvents=true&orderBy=startTime&maxResults=10&eventTypes=default&timeMin=${encodeURIComponent(timeMin)}&timeMax=${encodeURIComponent(timeMax)}`;

  const response = await doFetch(url, {
    headers: { Authorization: `Bearer ${input.accessToken}` },
  });
  if (!response.ok) {
    return { kind: "error", status: response.status, message: await response.text() };
  }
  const data = (await response.json()) as { items?: GoogleEvent[] };
  // Ceinture et bretelles : le filtre serveur ci-dessus devrait suffire, mais un
  // type non-ordinaire qui passerait quand même ne doit pas devenir le contexte
  // d'une capture. Un `eventType` ABSENT reste accepté — l'API ne le renseigne
  // pas partout, et l'écarter ferait disparaître de vraies rencontres.
  const ordinaires = (data.items ?? []).filter(
    (e) => e.eventType === undefined || e.eventType === "default",
  );
  const meeting = pickCurrentEvent(ordinaires, now, windowMin);
  return meeting ? { kind: "found", meeting } : { kind: "none" };
}
